import Button from '../../../components/ui/Button';
import { FormFieldDef, FormConfigSectionOptions } from '../../../interfaces/form-config.interface';
import { SectionSelect } from './SectionSelect';

interface SectionMapTabProps {
  fields: FormFieldDef[];
  sectionOptions: FormConfigSectionOptions;
  dirty: boolean;
  saving: boolean;
  onSectionChange: (fieldKey: string, col: 'listingSection' | 'detailSection', value: string | null) => void;
  onSave: () => void;
}

export const SectionMapTab = ({
  fields,
  sectionOptions,
  dirty,
  saving,
  onSectionChange,
  onSave,
}: SectionMapTabProps) => {
  const sortedFields = [...fields].sort((a, b) => a.step - b.step || a.order - b.order);

  // Merge managed options with values already assigned to fields, so
  // the dropdown always shows all usable sections even before seeding.
  const listingOptionValues = Array.from(new Set([
    ...sectionOptions.listing.map((o) => o.value),
    ...sortedFields.map((f) => f.listingSection).filter(Boolean) as string[],
  ]));
  const detailOptionValues = Array.from(new Set([
    ...sectionOptions.detail.map((o) => o.value),
    ...sortedFields.map((f) => f.detailSection).filter(Boolean) as string[],
  ]));

  return (
    <div className="flex flex-col flex-1 overflow-hidden">

      {/* ── Part 1: Section map table ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50 dark:bg-gray-800 text-left">
                <th className="px-3 py-2 font-medium text-gray-500 dark:text-gray-400 text-xs w-8">#</th>
                <th className="px-3 py-2 font-medium text-gray-500 dark:text-gray-400 text-xs w-40">Key</th>
                <th className="px-3 py-2 font-medium text-gray-500 dark:text-gray-400 text-xs">Label (EN)</th>
                <th className="px-3 py-2 font-medium text-gray-500 dark:text-gray-400 text-xs w-48">Listing Section</th>
                <th className="px-3 py-2 font-medium text-gray-500 dark:text-gray-400 text-xs w-48">Detail Section</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {sortedFields.map((field, idx) => (
                <tr
                  key={field.key}
                  className={`hover:bg-gray-50/50 dark:hover:bg-gray-800/30 ${
                    field.isActive === false ? 'opacity-50' : ''
                  }`}
                >
                  <td className="px-3 py-1.5 text-xs text-gray-400">{idx + 1}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1">
                      {(field.listingSection === 'Listing_Title' || field.detailSection === 'Info_Title') && (
                        <span title="Title anchor — cannot be reassigned" className="text-gray-400 text-[10px]">🔒</span>
                      )}
                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{field.key}</span>
                    </div>
                    <div className="text-[10px] text-gray-400">
                      <span className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-medium">
                        S{field.step}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300">
                    {field.label}
                  </td>
                  <td className="px-2 py-1">
                    <SectionSelect
                      label=""
                      value={field.listingSection}
                      options={listingOptionValues}
                      locked={field.listingSection === 'Listing_Title'}
                      onChange={(v) => onSectionChange(field.key, 'listingSection', v)}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <SectionSelect
                      label=""
                      value={field.detailSection}
                      options={detailOptionValues}
                      locked={field.detailSection === 'Info_Title'}
                      onChange={(v) => onSectionChange(field.key, 'detailSection', v)}
                    />
                  </td>
                </tr>
              ))}
              {sortedFields.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-sm text-gray-400">
                    No fields configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Part 2: Save bar ── */}
      <div className="shrink-0 flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <span className={`text-xs ${dirty ? 'text-yellow-600 dark:text-yellow-400 font-medium' : 'text-gray-400'}`}>
          {dirty ? 'Unsaved changes' : 'All saved'}
        </span>
        <Button
          variant="primary"
          size="sm"
          disabled={!dirty || saving}
          onClick={onSave}
        >
          {saving ? 'Saving…' : 'Save Section Map'}
        </Button>
      </div>

    </div>
  );
};

export default SectionMapTab;
